import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const MAX_VIDEOS = 2;

const VideoSubmissionForm = ({ artistId, artistName, onVideoAdded, currentVideoCount = 0 }) => {
  const [formData, setFormData] = useState({
    video_url: '',
    video_title: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const remainingSlots = Math.max(MAX_VIDEOS - currentVideoCount, 0);
  const limitReached = currentVideoCount >= MAX_VIDEOS;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (limitReached) {
      setError('You can add a maximum of 2 performance videos for now.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      const response = await axios.post(
        `${API}/videos/submit`,
        {
          artist_id: artistId,
          artist_name: artistName,
          video_url: formData.video_url,
          video_title: formData.video_title,
          description: formData.description
        },
        { withCredentials: true }
      );

      setSuccess(true);
      setFormData({ video_url: '', video_title: '', description: '' });

      if (onVideoAdded) {
        onVideoAdded(response.data);
      }

      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit video. Please check your public video URL.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="video-submission-form">
      <h3 className="text-2xl font-bold gradient-text mb-3">Add Your Performance Video</h3>
      <p className="text-sm text-gray-600 mb-6">
        You can add up to {MAX_VIDEOS} public performance videos. {remainingSlots > 0 ? `${remainingSlots} slot${remainingSlots === 1 ? '' : 's'} left.` : 'You have reached the limit.'}
      </p>

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4" data-testid="success-message">
          Video added successfully.
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" data-testid="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="video_url" className="block text-sm font-medium text-gray-700 mb-2">
            Video URL *
          </label>
          <input
            type="url"
            id="video_url"
            name="video_url"
            data-testid="video-url-input"
            value={formData.video_url}
            onChange={handleChange}
            required
            disabled={limitReached}
            placeholder="YouTube, Instagram, Facebook, Vimeo, or public video URL"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6F00] focus:border-transparent outline-none transition-all text-base"
          />
          <p className="text-xs text-gray-500 mt-1">YouTube embeds on profile. Instagram/Facebook and other public video links open as platform cards.</p>
        </div>

        <div className="mb-4">
          <label htmlFor="video_title" className="block text-sm font-medium text-gray-700 mb-2">
            Video Title *
          </label>
          <input
            type="text"
            id="video_title"
            name="video_title"
            data-testid="video-title-input"
            value={formData.video_title}
            onChange={handleChange}
            required
            disabled={limitReached}
            placeholder="e.g., Classical Dance Performance"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6F00] focus:border-transparent outline-none transition-all text-base"
          />
        </div>

        <div className="mb-6">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description (Optional)
          </label>
          <textarea
            id="description"
            name="description"
            data-testid="video-description-input"
            value={formData.description}
            onChange={handleChange}
            rows="3"
            disabled={limitReached}
            placeholder="Brief description of your performance..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6F00] focus:border-transparent outline-none transition-all resize-none text-base"
          ></textarea>
        </div>

        <button
          type="submit"
          data-testid="submit-video-button"
          disabled={isSubmitting || limitReached}
          className={`btn-primary w-full ${(isSubmitting || limitReached) ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {limitReached ? 'Video Limit Reached' : isSubmitting ? 'Submitting...' : 'Add Video'}
        </button>
      </form>
    </div>
  );
};

export default VideoSubmissionForm;
